import { useState } from 'react';
import { CheckIcon, CopyIcon, RefreshCwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { InvitationResponse, InvitationStatus } from '@/schema/invitation';
import { useDeleteInvitation, useResendInvitation, useRevokeInvitation } from '@/hooks/useInvitations';
import { useIsAdmin } from '@/hooks/useAdmin';
import { Column, DataTable } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatDate, formatRelative } from '../lib/format';

const BADGE_BASE =
  'inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]';

const STATUS_STYLES: Record<InvitationStatus, string> = {
  PENDING: `${BADGE_BASE} border-primary/40 bg-primary/15 text-primary-200`,
  ACCEPTED: `${BADGE_BASE} border-emerald-500/40 bg-emerald-500/15 text-emerald-200`,
  REVOKED: `${BADGE_BASE} border-white/15 bg-white/5 text-white/55`,
  EXPIRED: `${BADGE_BASE} border-amber-500/40 bg-amber-500/15 text-amber-200`,
};

interface InvitationTableProps {
  invitations: InvitationResponse[];
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function InvitationTable({
  invitations,
  page = 0,
  totalPages = 0,
  onPageChange,
  isLoading,
  emptyMessage = 'No invitations yet.',
}: InvitationTableProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<InvitationResponse | null>(null);
  const { isSuperAdmin } = useIsAdmin();
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  const remove = useDeleteInvitation();

  const copyLink = async (invitation: InvitationResponse) => {
    try {
      await navigator.clipboard.writeText(invitation.inviteUrl);
      setCopiedId(invitation.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  const columns: Column<InvitationResponse>[] = [
    {
      key: 'person',
      header: 'Person',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">
            {[row.firstName, row.lastName].filter(Boolean).join(' ') || row.email}
          </p>
          <p className="truncate text-xs text-white/45">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'clinic',
      header: 'Clinic / role',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-white/80">{row.clinicName ?? '—'}</p>
          <p className="truncate text-xs text-white/45">{row.role}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <span className={STATUS_STYLES[row.status]}>{row.status}</span>
          <span className="text-xs text-white/45">
            {row.status === 'ACCEPTED'
              ? `accepted ${formatRelative(row.acceptedAt)}`
              : `expires ${formatDate(row.expiresAt)}`}
          </span>
        </div>
      ),
    },
    {
      key: 'invitedBy',
      header: 'Invited by',
      render: (row) => <span className="text-sm text-white/70">{row.invitedByEmail ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      // Issuing and withdrawing accounts is the superadmin's; an admin only reads the list.
      render: (row) => {
        if (!isSuperAdmin) return null;
        // An accepted invitation is history: its link is spent and there is nothing to
        // revoke, but the row can still be cleared away.
        const isSpent = row.status === 'ACCEPTED';
        return (
          <div className="flex items-center justify-end gap-1">
            {!isSpent && (
              <>
                <Button
                  type="button"
                  variant="ghost-light"
                  size="sm"
                  onClick={() => copyLink(row)}
                  title="Copy invite link"
                >
                  {copiedId === row.id ? <CheckIcon /> : <CopyIcon />}
                </Button>
                <Button
                  type="button"
                  variant="ghost-light"
                  size="sm"
                  onClick={() => resend.mutate(row.id)}
                  title="Issue a new link"
                >
                  <RefreshCwIcon className={cn(resend.isPending && 'animate-spin')} />
                </Button>
              </>
            )}
            {row.status === 'PENDING' ? (
              <Button
                type="button"
                variant="ghost-danger"
                size="sm"
                onClick={() => revoke.mutate(row.id)}
                title="Revoke"
              >
                <XIcon />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost-danger"
              size="sm"
              onClick={() => setPendingDelete(row)}
              title="Delete this invitation"
            >
              <Trash2Icon />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={invitations}
        rowKey={(row) => row.id}
        page={page}
        totalPages={onPageChange ? totalPages : 0}
        onPageChange={onPageChange ?? (() => {})}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
      />

      {pendingDelete && (
        <ConfirmDialog
          title="Delete invitation"
          confirmLabel="Delete invitation"
          isPending={remove.isPending}
          error={remove.error?.message}
          onClose={() => setPendingDelete(null)}
          onConfirm={() =>
            remove.mutate(pendingDelete.id, {
              onSuccess: () => {
                setPendingDelete(null);
                toast.success('Invitation deleted');
              },
            })
          }
        >
          <p>
            The invitation for <strong>{pendingDelete.email}</strong> disappears from this list.
          </p>
          {pendingDelete.status === 'PENDING' && (
            <p className="text-white/50">
              Revoking keeps the record and only stops the link from working.
            </p>
          )}
        </ConfirmDialog>
      )}
    </>
  );
}
